/**
 * Voice-note dictation service — TASK-476.
 * Two standalone steps proxied to the meeting-orchestrator (gateway: /api/voice-note/*).
 * `structure` PROPOSES only; the caller commits approved items via the existing
 * clientService / knowledgeGraphService.
 */
import { httpClient, httpClientUpload } from '../lib/httpClient';
import type { ProposalItem } from '../components/kyc/workspace/ExtractionProposal';

export interface VoiceNoteTask {
  title: string;
  description?: string;
  priority?: string;
  due_date?: string | null;
  /**
   * TASK-701 — the extractor's classification, normalised server-side against the canonical
   * `TaskType` enum. Optional because an older server build omits it; the note endpoint then
   * applies the same default rather than the UI guessing a label.
   */
  task_type?: string;
}

export interface StructureResult {
  note_id: string;
  note_draft: { summary: string; topics: string[] };
  dna_proposals: ProposalItem[];
  tasks: VoiceNoteTask[];
  // TASK-668 — set when the dictation is bound to a meeting.
  session_id?: string | null;
  source_type?: 'rm_dictation' | 'voice_note';
  /** Verbatim label the UI must show on meeting-bound output. */
  provenance_label?: string | null;
  finsa_notes?: unknown;
}

export async function transcribeVoiceNote(
  blob: Blob,
  language?: string
): Promise<{ transcript: string; language: string | null; confidence: number | null }> {
  const form = new FormData();
  form.append('audio', blob, 'voice-note.webm');
  if (language) form.append('language', language);
  // Setting Content-Type to undefined lets axios derive multipart/form-data + boundary
  // (the shared client defaults to application/json). Same trick as meetingChunkUpload.
  return httpClientUpload.post('/api/voice-note/transcribe', form, {
    headers: { 'Content-Type': undefined as unknown as string },
  });
}

export async function structureVoiceNote(
  clientId: string,
  transcript: string,
  clientName?: string,
  language = 'en',
  /** TASK-668 — bind this dictation to a meeting. Omit for a standalone client note. */
  sessionId?: string
): Promise<StructureResult> {
  return httpClient.post('/api/voice-note/structure', {
    client_id: clientId,
    transcript,
    client_name: clientName ?? 'the client',
    language,
    session_id: sessionId ?? null,
  });
}

/**
 * TASK-689 — a PHOTO of a handwritten note, structured through the same skill fan-out a
 * dictation uses (review fix: the endpoint shipped with no caller, so a fact captured on
 * paper still never reached the graph).
 *
 * Returns the identical shape as `structureVoiceNote`, plus `readable`: an unreadable image
 * comes back `{readable: false, reason}` with NO proposals — never a low-confidence OCR
 * guess dressed as fact. Persists nothing; the RM approves through the same L1/L3 path.
 */
export interface PhotoNoteResult extends StructureResult {
  /** false = the image could not be read. No proposals come with it, by design. */
  readable?: boolean;
  reason?: string | null;
  /** What the vision model read off the paper, verbatim — the RM's chance to correct it. */
  transcript?: string;
}

export async function structurePhotoNote(
  clientId: string,
  file: File,
  clientName?: string,
  language = 'en',
  sessionId?: string
): Promise<PhotoNoteResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('client_id', clientId);
  form.append('client_name', clientName ?? 'the client');
  form.append('language', language);
  if (sessionId) form.append('session_id', sessionId);
  return httpClientUpload.post('/api/voice-note/structure-photo', form, {
    headers: { 'Content-Type': undefined as unknown as string },
  });
}

/**
 * TASK-668 — mark a meeting documented. Called ONLY after the RM approves; `/structure`
 * never sets this, so a generated-but-unapproved dictation cannot make a meeting look
 * documented. Idempotent server-side (first approval's actor/timestamp survive).
 */
export interface ApprovedDictation {
  /** The dictation exactly as the RM approved it — edits included. */
  transcript_text: string;
  /** Verbatim honesty label; stored alongside the text so no reader loses it. */
  provenance_label?: string | null;
  /** Structured `/structure` output. Generated suggestions the RM approved. */
  finsa_notes?: unknown;
  structure_schema_version?: string;
}

export interface DocumentMeetingResult {
  documented_at: string | null;
  documentation_source: string;
  /** TASK-700 — the terminal lifecycle state the approval produced. */
  status?: string;
  ended_at?: string | null;
  /** TASK-704 — whether the approved material is now persisted. */
  documentation_recorded?: boolean;
  /** True when a SECOND dictation was refused so the first approved record stands. */
  documentation_superseded_attempt?: boolean;
  batch_enqueued?: boolean;
}

/**
 * TASK-668 — mark a meeting documented. Called ONLY after the RM approves; `/structure`
 * never sets this, so a generated-but-unapproved dictation cannot make a meeting look
 * documented. Idempotent server-side (first approval's actor/timestamp survive).
 *
 * TASK-704 — it now also carries the material to persist. `sourceNoteId` is the idempotency
 * key: the client retries this call after the note has landed, and the same key is a no-op.
 */
export async function documentMeetingByDictation(
  sessionId: string,
  clientId: string,
  sourceNoteId: string,
  approved?: ApprovedDictation
): Promise<DocumentMeetingResult> {
  return httpClient.post('/api/voice-note/document-meeting', {
    session_id: sessionId,
    client_id: clientId,
    source_note_id: sourceNoteId,
    ...(approved ?? {}),
  });
}
